import React, { useState } from 'react';
import { Github, Brain, Server, Cpu, MessageSquare, ArrowRight, CheckCircle, ExternalLink } from 'lucide-react';

const ServiceCard = ({ icon: Icon, title, description, color }) => (
  <article className="bg-tf-blue-800/50 backdrop-blur p-6 rounded-xl border border-tf-blue-700/50 hover:border-tf-purple-500/50 transition-all duration-300 hover:transform hover:scale-[1.02]">
    <Icon className={`${color} mb-4`} size={40} aria-hidden="true" />
    <h3 className="text-xl font-semibold mb-2 text-white">{title}</h3>
    <p className="text-tf-blue-200">{description}</p>
  </article>
);

const CaseStudyCard = ({ title, client, description, tags, url }) => (
  <article className="bg-white/5 backdrop-blur p-6 rounded-xl border border-tf-blue-700/30 hover:border-tf-cyan-500/50 transition-all">
    <div className="flex items-start justify-between mb-3">
      <h3 className="text-lg font-semibold text-white">{title}</h3>
      {url && (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-tf-cyan-400 hover:text-tf-cyan-300" aria-label={`Visit ${title}`}>
          <ExternalLink size={18} aria-hidden="true" />
        </a>
      )}
    </div>
    <p className="text-tf-purple-300 text-sm mb-2">{client}</p>
    <p className="text-tf-blue-200 text-sm mb-4">{description}</p>
    <div className="flex flex-wrap gap-2">
      {tags.map((tag, idx) => (
        <span key={idx} className="text-xs px-2 py-1 bg-tf-blue-900/50 text-tf-cyan-400 rounded-full">
          {tag}
        </span>
      ))}
    </div>
  </article>
);

const Home = () => {
  const [formStatus, setFormStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const services = [
    {
      icon: Brain,
      title: "AI/ML Engineering",
      description: "Custom model development, fine-tuning, and deployment. From LLMs to computer vision, we build AI solutions that deliver results.",
      color: "text-tf-purple-400"
    },
    {
      icon: Server,
      title: "MLOps & Infrastructure",
      description: "Production-ready AI pipelines, GPU orchestration, and scalable inference platforms. Built on Kubernetes with enterprise reliability.",
      color: "text-tf-cyan-400"
    },
    {
      icon: Cpu,
      title: "Architecture & Design",
      description: "Strategic AI architecture consulting. RAG systems, agent frameworks, and hybrid cloud solutions designed for your scale.",
      color: "text-tf-blue-400"
    },
    {
      icon: MessageSquare,
      title: "AI Integration",
      description: "Seamless integration of AI capabilities into existing products. APIs, chat interfaces, and intelligent automation.",
      color: "text-green-400"
    },
  ];

  const caseStudies = [
    {
      title: "SolidRusT.AI Platform",
      client: "SolidRusT Networks",
      description: "Built a complete AI inference platform with vLLM, LiteLLM proxy, and automatic failover. Serves chat completions and embeddings at scale.",
      tags: ["vLLM", "Kubernetes", "LiteLLM", "RAG"],
      url: "https://solidrust.ai"
    },
    {
      title: "MyAshes.ai Gaming Assistant",
      client: "SolidRusT Networks",
      description: "AI-powered gaming companion for Ashes of Creation with real-time game knowledge and community integration.",
      tags: ["LLM", "RAG", "Gaming", "Real-time"],
      url: "https://myashes.ai"
    },
    {
      title: "AIdiant Member Platform",
      client: "SolidRusT Networks",
      description: "Per-member AI assistants with tool-calling capabilities, custom embeddings, and personalized context.",
      tags: ["Agents", "Embeddings", "Personalization"],
      url: "https://aidiant.com"
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const form = e.target;
    const data = new FormData(form);

    try {
      const response = await fetch('https://formspree.io/f/xyzyvzkd', {
        method: 'POST',
        body: data,
        headers: { 'Accept': 'application/json' }
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    }

    setIsSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-tf-blue-950 via-tf-blue-900 to-tf-purple-950 text-white">
      {/* Header */}
      <header className="bg-tf-blue-950/80 backdrop-blur-sm border-b border-tf-blue-800/50 sticky top-0 z-50" role="banner">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold bg-gradient-to-r from-tf-blue-400 to-tf-purple-400 bg-clip-text text-transparent">
            TechFusion
          </h1>
          <nav className="flex items-center gap-6" aria-label="Main navigation">
            <a href="#services" className="text-tf-blue-200 hover:text-white transition-colors">Services</a>
            <a href="#work" className="text-tf-blue-200 hover:text-white transition-colors">Work</a>
            <a href="#contact" className="text-tf-blue-200 hover:text-white transition-colors">Contact</a>
            <a
              href="https://github.com/SolidRusT"
              className="text-tf-blue-200 hover:text-white transition-colors"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="View SolidRusT on GitHub"
            >
              <Github size={20} aria-hidden="true" />
            </a>
          </nav>
        </div>
      </header>

      <main role="main">
        {/* Hero */}
        <section className="container mx-auto px-4 py-20 text-center" aria-labelledby="hero-heading">
          <div className="max-w-4xl mx-auto">
            <p className="text-tf-purple-400 font-medium mb-4">AI/ML Consultancy</p>
            <h2 id="hero-heading" className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
              We Build{' '}
              <span className="bg-gradient-to-r from-tf-cyan-400 to-tf-purple-400 bg-clip-text text-transparent">
                Intelligent Systems
              </span>
            </h2>
            <p className="text-xl text-tf-blue-200 max-w-2xl mx-auto mb-8">
              From concept to production, we design and deploy AI solutions that transform how businesses operate.
              Custom LLMs, RAG pipelines, and enterprise-grade infrastructure.
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-tf-blue-500 to-tf-purple-500 hover:from-tf-blue-400 hover:to-tf-purple-400 px-6 py-3 rounded-lg font-medium transition-all"
              >
                Start a Project <ArrowRight size={18} />
              </a>
              <a
                href="#work"
                className="inline-flex items-center gap-2 border border-tf-blue-500 hover:bg-tf-blue-500/20 px-6 py-3 rounded-lg font-medium transition-all"
              >
                View Our Work
              </a>
            </div>
          </div>
        </section>

        {/* Services */}
        <section id="services" className="container mx-auto px-4 py-16" aria-labelledby="services-heading">
          <h2 id="services-heading" className="text-3xl font-bold text-center mb-4">What We Do</h2>
          <p className="text-tf-blue-200 text-center max-w-2xl mx-auto mb-12">
            End-to-end AI consulting for startups and enterprises. We handle the complexity so you can focus on your business.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <ServiceCard key={index} {...service} />
            ))}
          </div>
        </section>

        {/* Case Studies */}
        <section id="work" className="container mx-auto px-4 py-16" aria-labelledby="work-heading">
          <h2 id="work-heading" className="text-3xl font-bold text-center mb-4">Our Work</h2>
          <p className="text-tf-blue-200 text-center max-w-2xl mx-auto mb-12">
            Real-world AI solutions we've built and deployed. Each project demonstrates our expertise in production AI systems.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {caseStudies.map((study, index) => (
              <CaseStudyCard key={index} {...study} />
            ))}
          </div>
        </section>

        {/* About / Parent Company */}
        <section className="container mx-auto px-4 py-16" aria-labelledby="about-heading">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-tf-blue-800/30 to-tf-purple-800/30 backdrop-blur p-8 rounded-2xl border border-tf-blue-700/30">
            <h2 id="about-heading" className="text-2xl font-bold mb-4">Backed by SolidRusT Networks</h2>
            <p className="text-tf-blue-200 mb-6">
              TechFusion is a division of SolidRusT Networks, providing specialized AI/ML consulting services.
              Our parent company owns and operates the infrastructure - Kubernetes clusters, GPU nodes,
              and the complete AI stack - so our consultancy can focus on what matters: delivering intelligent solutions.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="https://solidrust.net"
                className="inline-flex items-center gap-2 text-tf-cyan-400 hover:text-tf-cyan-300 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <CheckCircle size={18} /> SolidRusT Networks
              </a>
              <a
                href="https://solidrust.ai"
                className="inline-flex items-center gap-2 text-tf-cyan-400 hover:text-tf-cyan-300 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <CheckCircle size={18} /> AI Platform
              </a>
              <a
                href="https://github.com/SolidRusT"
                className="inline-flex items-center gap-2 text-tf-cyan-400 hover:text-tf-cyan-300 transition-colors"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Github size={18} /> Open Source
              </a>
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section id="contact" className="container mx-auto px-4 py-16" aria-labelledby="contact-heading">
          <div className="max-w-2xl mx-auto">
            <h2 id="contact-heading" className="text-3xl font-bold text-center mb-4">Let's Build Something</h2>
            <p className="text-tf-blue-200 text-center mb-8">
              Have an AI project in mind? Tell us about it and we'll get back to you within 24 hours.
            </p>

            {formStatus === 'success' ? (
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-6 text-center">
                <CheckCircle className="mx-auto mb-3 text-green-400" size={48} />
                <h3 className="text-xl font-semibold mb-2">Message Sent!</h3>
                <p className="text-tf-blue-200">We'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 bg-tf-blue-900/50 border border-tf-blue-700 rounded-lg focus:outline-none focus:border-tf-purple-500 transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium mb-2">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 bg-tf-blue-900/50 border border-tf-blue-700 rounded-lg focus:outline-none focus:border-tf-purple-500 transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium mb-2">Company (optional)</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 bg-tf-blue-900/50 border border-tf-blue-700 rounded-lg focus:outline-none focus:border-tf-purple-500 transition-colors"
                    placeholder="Your company"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium mb-2">Tell us about your project</label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    className="w-full px-4 py-3 bg-tf-blue-900/50 border border-tf-blue-700 rounded-lg focus:outline-none focus:border-tf-purple-500 transition-colors resize-none"
                    placeholder="What are you looking to build? What challenges are you facing?"
                  />
                </div>
                {formStatus === 'error' && (
                  <p className="text-red-400 text-sm">Something went wrong. Please try again or email us directly.</p>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-tf-blue-500 to-tf-purple-500 hover:from-tf-blue-400 hover:to-tf-purple-400 disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 rounded-lg font-medium transition-all"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-tf-blue-950 border-t border-tf-blue-800/50 py-8" role="contentinfo">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-tf-blue-400 text-sm">
              &copy; {new Date().getFullYear()} TechFusion, a division of SolidRusT Networks
            </p>
            <div className="flex gap-6 text-sm">
              <a href="https://solidrust.ai" className="text-tf-blue-300 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
                AI Platform
              </a>
              <a href="https://solidrust.net" className="text-tf-blue-300 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
                Community
              </a>
              <a href="https://github.com/SolidRusT" className="text-tf-blue-300 hover:text-white transition-colors" target="_blank" rel="noopener noreferrer">
                GitHub
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
